import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button, Select, Table, Modal, message, Row, Col, Card, Statistic } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { allapiAction } from "../../Redux/common/action";
import moment from "moment";
import { GetUser, Logout } from "../utils";

function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const user = GetUser();

  const attendanceStatus = useSelector(
    (state) => (state?.allapi?.attendance_status ? state?.allapi?.attendance_status : null)
  );
  const loading = useSelector(
    (state) => (state?.allapi?.loading ? state?.allapi?.loading : false)
  );
  const assignedOrderSummary = useSelector(
    (state) => (state?.allapi?.assigned_order_list?.summary ? state?.allapi?.assigned_order_list?.summary : {})
  );

  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [cameraAction, setCameraAction] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    dispatch(allapiAction.getDeliveryPartnerProfile());
    dispatch(allapiAction.getAttendanceStatus());
    dispatch(allapiAction.getAssignedOrderList(1));
    return () => {};
  }, [location]);

  useEffect(() => {
    if (!cameraModalVisible && cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  }, [cameraModalVisible]);

  const isCheckedIn = attendanceStatus?.inTime && !attendanceStatus?.outTime;
  const isCheckedOut = attendanceStatus?.outTime;
  const attendanceDate = attendanceStatus?.date
    ? moment(attendanceStatus.date).format("DD/MM/YYYY")
    : moment().format("DD/MM/YYYY");

  const handleMarkIn = () => {
    setCameraAction("in");
    setCameraModalVisible(true);
  };

  const handleMarkOut = () => {
    setCameraAction("out");
    setCameraModalVisible(true);
  };

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
        audio: false,
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      message.error("Camera access denied. Please allow camera permission in browser settings.");
    }
  };

  const switchCamera = () => {
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setTimeout(() => initCamera(), 100);
  };

  useEffect(() => {
    if (cameraModalVisible && !capturedPhoto) {
      initCamera();
    }
    return () => {
      if (cameraStream && !cameraModalVisible) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraModalVisible, capturedPhoto]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      setCapturedPhoto(dataUrl);
    }
  };

  const handleSubmitAttendance = async () => {
    if (!capturedPhoto) {
      message.warning("Please capture a photo first");
      return;
    }

    const getLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 5000 }
        );
      });

    const location = await getLocation();
    const data = { photo: capturedPhoto, location };

    try {
      if (cameraAction === "in") {
        await dispatch(allapiAction.markAttendanceIn(data)).unwrap();
        message.success("Checked In successfully!");
      } else if (cameraAction === "out") {
        await dispatch(allapiAction.markAttendanceOut(data)).unwrap();
        message.success("Checked Out successfully!");
      }
      setCameraModalVisible(false);
      setCapturedPhoto(null);
      setCameraAction(null);
      dispatch(allapiAction.getAttendanceStatus());
    } catch (err) {
      message.error(err?.message || "Failed to mark attendance");
    }
  };

  const handleCloseCameraModal = () => {
    setCameraModalVisible(false);
    setCapturedPhoto(null);
    setCameraAction(null);
  };

  const handleLogout = () => {
    Logout();
    navigate("/login");
  };

  const totalAssigned = assignedOrderSummary?.totalAssigned ? assignedOrderSummary?.totalAssigned : 0;
  const totalDelivered = assignedOrderSummary?.totalDelivered ? assignedOrderSummary?.totalDelivered : 0;
  const totalAccepted = assignedOrderSummary?.totalAccepted ? assignedOrderSummary?.totalAccepted : 0;
  const totalOutForDelivery = assignedOrderSummary?.totalOutForDelivery ? assignedOrderSummary?.totalOutForDelivery : 0;

  const summaryCards = [
    { title: "Assigned", value: totalAssigned, color: "#096dd9", icon: "📦" },
    { title: "Accepted", value: totalAccepted, color: "#fa8c16", icon: "✅" },
    { title: "Out for Delivery", value: totalOutForDelivery, color: "#722ed1", icon: "🚚" },
    { title: "Delivered", value: totalDelivered, color: "#389e0d", icon: "✓" },
  ];

  const getAttendanceStatusColor = () => {
    if (isCheckedOut) return "#52c41a";
    if (isCheckedIn) return "#faad14";
    return "#f5222d";
  };

  const getAttendanceStatusText = () => {
    if (isCheckedOut) return "Completed";
    if (isCheckedIn) return "Checked In";
    return "Not Started";
  };

  return (
    <div className="dashboard-page">
      <div className="delivery-header">
        <div className="header-left">
          <h4>Dashboard</h4>
          <span className="partner-name">
            {user?.fullName} ({user?.phone})
          </span>
        </div>
        <div className="header-right">
          <Link to="/orders" className="nav-btn orders-btn">
            📦 My Orders
          </Link>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="main-container">
        <div className="attendance-section mb-20">
          <Card className="attendance-card" style={{ borderLeft: `5px solid ${getAttendanceStatusColor()}` }}>
            <Row align="middle" justify="space-between">
              <Col xs={24} md={12}>
                <Row align="middle" gutter={16}>
                  <Col xs={12} md={6}>
                    <div className="attendance-icon" style={{ background: getAttendanceStatusColor() }}>
                      {isCheckedOut ? "✓" : isCheckedIn ? "⏱" : "📴"}
                    </div>
                  </Col>
                  <Col xs={12} md={18}>
                    <div className="attendance-title">Today's Attendance</div>
                    <div className="attendance-date">{attendanceDate}</div>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={12} style={{ textAlign: "right" }}>
                <div className="attendance-status" style={{ color: getAttendanceStatusColor() }}>
                  {getAttendanceStatusText()}
                </div>
                <div className="attendance-times" style={{ marginTop: 8 }}>
                  {attendanceStatus?.inTime && (
                    <>
                      <span className="time-badge">In: {moment(attendanceStatus.inTime).format("HH:mm:ss")}</span>
                      {attendanceStatus?.outTime && (
                        <span className="time-badge" style={{ marginLeft: 8 }}>Out: {moment(attendanceStatus.outTime).format("HH:mm:ss")}</span>
                      )}
                    </>
                  )}
                  {!attendanceStatus?.inTime && (
                    <span className="time-badge" style={{ background: "#fff0f0", color: "#ff4d4f" }}>Not started yet</span>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        <div className="action-section mb-20">
          <Card className="action-card">
            <div className="action-title">Quick Actions</div>
            <Row gutter={16} justify="center">
              <Col xs={24} sm={12} md={6}>
                {!isCheckedIn && !isCheckedOut && (
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handleMarkIn}
                    className="action-btn checkin-btn"
                    style={{ height: 70, fontSize: 16, fontWeight: 600 }}
                  >
                    📸 Check In
                  </Button>
                )}
                {isCheckedIn && !isCheckedOut && (
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handleMarkOut}
                    className="action-btn checkout-btn"
                    style={{ height: 70, fontSize: 16, fontWeight: 600, background: "#f5222d", borderColor: "#f5222d" }}
                  >
                    📸 Check Out
                  </Button>
                )}
                {isCheckedOut && (
                  <Button
                    size="large"
                    block
                    disabled
                    className="action-btn completed-btn"
                    style={{ height: 70, fontSize: 16, fontWeight: 600 }}
                  >
                    ✓ Completed for Today
                  </Button>
                )}
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Link to="/orders">
                  <Button
                    size="large"
                    block
                    className="action-btn orders-btn"
                    style={{ height: 70, fontSize: 16, fontWeight: 600 }}
                  >
                    📦 View My Orders
                  </Button>
                </Link>
              </Col>
            </Row>
          </Card>
        </div>

        <div className="summary-section">
          <div className="summary-title mb-10">Order Summary</div>
          <Row gutter={16}>
            {summaryCards.map((card, i) => (
              <Col xs={12} sm={6} md={3} key={i}>
                <Card className="summary-card" style={{ borderLeft: `4px solid ${card.color}` }}>
                  <div className="summary-card-header">
                    <span className="summary-icon">{card.icon}</span>
                    <span className="summary-card-title">{card.title}</span>
                  </div>
                  <Statistic
                    value={card.value}
                    valueStyle={{ color: card.color, fontSize: 28, fontWeight: 700 }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      <Modal
        title={`Capture Photo for ${cameraAction === "in" ? "Check In" : "Check Out"}`}
        visible={cameraModalVisible}
        onCancel={handleCloseCameraModal}
        footer={null}
        width={640}
        centered
        destroyOnClose
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          {!capturedPhoto ? (
            <>
              <div className="camera-preview" style={{ position: "relative" }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: "100%", maxHeight: "420px", background: "#000", borderRadius: 8 }}
                />
                <canvas ref={canvasRef} style={{ display: "none" }} />
                <Button
                  type="default"
                  size="small"
                  style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    background: "rgba(0,0,0,0.6)",
                    borderColor: "rgba(255,255,255,0.3)",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "4px 12px",
                  }}
                  onClick={switchCamera}
                  title={facingMode === "environment" ? "Switch to Front Camera" : "Switch to Rear Camera"}
                >
                  🔄 {facingMode === "environment" ? "Front" : "Rear"}
                </Button>
              </div>
              <div style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={capturePhoto}
                  style={{ marginRight: 12, minWidth: 160 }}
                >
                  📷 Capture Photo
                </Button>
                <Button size="large" onClick={handleCloseCameraModal} style={{ minWidth: 120 }}>
                  Cancel
                </Button>
              </div>
              <p style={{ marginTop: 12, fontSize: 12, color: "#999" }}>
                Please position your face clearly in the frame. Tap 🔄 to switch camera.
              </p>
            </>
          ) : (
            <>
              <div className="photo-preview">
                <img
                  src={capturedPhoto}
                  alt="Captured"
                  style={{ width: "100%", maxHeight: "420px", borderRadius: 8, border: "2px solid #d9d9d9", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
                />
              </div>
              <div style={{ marginTop: 20, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubmitAttendance}
                  style={{ minWidth: 140 }}
                >
                  ✓ Submit
                </Button>
                <Button size="large" onClick={() => setCapturedPhoto(null)} style={{ minWidth: 120 }}>
                  Retake
                </Button>
                <Button size="large" onClick={handleCloseCameraModal} style={{ minWidth: 120 }}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Dashboard;